import React, { useState, useEffect } from 'react';
import { getClinicEfficiencySettings, updateClinicEfficiencySettings } from '../api';

// Ключи критериев в БД (snake_case для API)
const CRITERIA_KEYS = {
    'greeting': 'Приветствие',
    'patient_name': 'Имя пациента',
    'needs_identification': 'Выявление потребностей',
    'service_presentation': 'Презентация услуги',
    'clinic_presentation': 'Презентация клиники',
    'doctor_presentation': 'Презентация врача',
    'appointment': 'Запись',
    'price': 'Цена',
    'expertise': 'Экспертность',
    'next_step': 'Следующий шаг',
    'appointment_booking': 'Запись на прием ⭐',
    'emotional_tone': 'Эмоциональный окрас',
    'speech': 'Речь',
    'initiative': 'Инициатива',
    'clinic_address': 'Адрес клиники',
    'passport': 'Паспорт',
    'objection_handling': 'Работа с возражениями',
    'appeal': 'Апелляция',
    'question_clarification': 'Уточнение вопроса',
};

// Типы звонков
const CALL_TYPES = [
    { id: 'первичка', label: '📞 Первичка' },
    { id: 'вторичка', label: '🔄 Вторичка' },
    { id: 'перезвон', label: '📲 Перезвон' },
    { id: 'подтверждение', label: '✅ Подтверждение' },
];

const EfficiencyModal = ({ show, onClose, clientId, onSave }) => {
    const [activeTab, setActiveTab] = useState('первичка');
    const [criteriaData, setCriteriaData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Загрузка критериев при открытии
    useEffect(() => {
        if (show && clientId) {
            loadCriteria();
        }
    }, [show, clientId]);

    const loadCriteria = async () => {
        setIsLoading(true);
        try {
            const data = await getClinicEfficiencySettings(clientId);

            // Преобразуем массив в объект по типам звонков
            const criteriaByType = {};
            if (Array.isArray(data)) {
                data.forEach(item => {
                    criteriaByType[item.call_type] = item;
                });
            } else if (data && typeof data === 'object') {
                // Если пришел объект (например, от обертки apiFetch), проверяем его структуру
                const items = data.data || data;
                if (Array.isArray(items)) {
                    items.forEach(item => {
                        criteriaByType[item.call_type] = item;
                    });
                }
            }

            // Заполняем недостающие типы
            CALL_TYPES.forEach(({ id }) => {
                if (!criteriaByType[id]) {
                    criteriaByType[id] = createEmptyCriteria(id);
                }
            });

            setCriteriaData(criteriaByType);
        } catch (error) {
            console.error('Error loading criteria:', error);
            // Создать пустую структуру
            const emptyData = {};
            CALL_TYPES.forEach(({ id }) => {
                emptyData[id] = createEmptyCriteria(id);
            });
            setCriteriaData(emptyData);
        } finally {
            setIsLoading(false);
        }
    };

    const createEmptyCriteria = (callType) => {
        const criteria = {
            call_type: callType,
            client_id: clientId,
            overall_effective_score: '',
            overall_ineffective_score: '',
        };
        Object.keys(CRITERIA_KEYS).forEach(key => {
            criteria[key] = { effective: '', ineffective: '' };
        });
        return criteria;
    };

    // Парсинг диапазона
    const parseRange = (val) => {
        if (!val) return null;
        const parts = val.toString().split('-').map(v => parseFloat(v?.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            return { min: parts[0], max: parts[1] };
        }
        if (parts.length === 1 && !isNaN(parts[0])) {
            return { min: parts[0], max: parts[0] };
        }
        return null;
    };

    // Авторасчет общего балла для конкретного типа звонка
    const calculateOverall = (callType) => {
        const data = criteriaData[callType];
        if (!data) return { effective: '', ineffective: '' };

        let effSumMin = 0, effSumMax = 0, effCount = 0;
        let ineffSumMin = 0, ineffSumMax = 0, ineffCount = 0;

        Object.keys(CRITERIA_KEYS).forEach(key => {
            const criterion = data[key];
            if (criterion?.effective) {
                const range = parseRange(criterion.effective);
                if (range) {
                    effSumMin += range.min;
                    effSumMax += range.max;
                    effCount++;
                }
            }
            if (criterion?.ineffective) {
                const range = parseRange(criterion.ineffective);
                if (range) {
                    ineffSumMin += range.min;
                    ineffSumMax += range.max;
                    ineffCount++;
                }
            }
        });

        const formatAvg = (minSum, maxSum, count) => {
            if (count === 0) return '';
            const avgMin = Math.round(minSum / count);
            const avgMax = Math.round(maxSum / count);
            return avgMin === avgMax ? `${avgMin}` : `${avgMin}-${avgMax}`;
        };

        return {
            effective: formatAvg(effSumMin, effSumMax, effCount),
            ineffective: formatAvg(ineffSumMin, ineffSumMax, ineffCount)
        };
    };

    // Обработка изменения критерия
    const handleCriterionChange = (criterionKey, field, value) => {
        const newData = { ...criteriaData };
        if (!newData[activeTab]) {
            newData[activeTab] = createEmptyCriteria(activeTab);
        }
        if (!newData[activeTab][criterionKey]) {
            newData[activeTab][criterionKey] = { effective: '', ineffective: '' };
        }
        newData[activeTab][criterionKey][field] = value;

        // Пересчитываем общие баллы
        const overall = calculateOverall(activeTab);
        newData[activeTab].overall_effective_score = overall.effective;
        newData[activeTab].overall_ineffective_score = overall.ineffective;

        setCriteriaData(newData);
    };

    // Сохранение
    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Сохраняем каждый тип звонка отдельно
            for (const callType of CALL_TYPES.map(t => t.id)) {
                const data = criteriaData[callType];
                if (!data) continue;

                const payload = {
                    clinic_id: clientId,
                    call_type: callType,
                    overall_effective_score: data.overall_effective_score || '',
                    overall_ineffective_score: data.overall_ineffective_score || '',
                };

                // Добавляем критерии
                Object.keys(CRITERIA_KEYS).forEach(key => {
                    payload[key] = data[key] || { effective: '', ineffective: '' };
                });

                // Используем общую функцию обновления из api.js (она добавит ключ и нужный URL)
                await updateClinicEfficiencySettings(clientId, payload);
            }

            alert('✅ Критерии успешно сохранены для всех типов звонков!');
            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error('Error saving criteria:', error);
            alert(`❌ Ошибка при сохранении критериев: ${error.message || 'Неизвестная ошибка'}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (!show) return null;

    const currentData = criteriaData[activeTab] || createEmptyCriteria(activeTab);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl mx-auto my-8 max-h-[95vh] overflow-hidden flex flex-col">
                {/* Заголовок */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
                    <h2 className="text-2xl font-bold flex items-center text-indigo-900">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mr-3 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Настройка критериев эффективности
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Табы типов звонков */}
                <div className="border-b border-gray-200 bg-gray-50 px-6">
                    <div className="flex space-x-1">
                        {CALL_TYPES.map(({ id, label }) => (
                            <button
                                key={id}
                                onClick={() => setActiveTab(id)}
                                className={`px-6 py-3 font-semibold transition-all ${activeTab === id
                                    ? 'bg-white text-indigo-600 border-t-4 border-t-indigo-600 -mb-px'
                                    : 'text-gray-600 hover:text-indigo-500 hover:bg-gray-100'
                                    }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center py-20">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                            <p className="text-gray-500">Загрузка настроек...</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Подсказка */}
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                            <div className="font-semibold mb-2">💡 Инструкция:</div>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Введите <strong>диапазон</strong> (например: "7-10") или <strong>одно число</strong> (например: "5")</li>
                                <li>Общие баллы <strong>рассчитываются автоматически</strong> как среднее значение всех критериев</li>
                                <li>Для каждого типа звонка настройки сохраняются <strong>отдельно</strong></li>
                            </ul>
                        </div>

                        {/* Общие баллы */}
                        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border-2 border-indigo-200">
                            <h3 className="font-bold text-lg text-indigo-900 mb-4 flex items-center">
                                📊 Общие баллы (автоматически)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-green-700 mb-2">
                                        ✅ Эффективный звонок
                                    </label>
                                    <input
                                        type="text"
                                        value={currentData.overall_effective_score || ''}
                                        readOnly
                                        placeholder="Расчет..."
                                        className="w-full px-4 py-3 border-2 border-green-300 rounded-lg bg-green-50 text-gray-700 font-semibold cursor-not-allowed"
                                        title="Рассчитывается автоматически на основе критериев"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-red-700 mb-2">
                                        ❌ Неэффективный звонок
                                    </label>
                                    <input
                                        type="text"
                                        value={currentData.overall_ineffective_score || ''}
                                        readOnly
                                        placeholder="Расчет..."
                                        className="w-full px-4 py-3 border-2 border-red-300 rounded-lg bg-red-50 text-gray-700 font-semibold cursor-not-allowed"
                                        title="Рассчитывается автоматически на основе критериев"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Критерии */}
                        <h3 className="font-bold text-lg text-gray-800 mb-4">📋 Критерии оценки</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.entries(CRITERIA_KEYS).map(([key, label]) => {
                                const criterionData = currentData[key] || { effective: '', ineffective: '' };
                                return (
                                    <div key={key} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                        <label className="block text-sm font-bold text-gray-700 mb-3 border-b border-gray-100 pb-2">
                                            {label}
                                        </label>
                                        <div className="space-y-3">
                                            <div>
                                                <span className="text-xs text-green-700 font-semibold uppercase block mb-1">
                                                    ✅ Эффективный
                                                </span>
                                                <input
                                                    type="text"
                                                    value={criterionData.effective || ''}
                                                    onChange={(e) => handleCriterionChange(key, 'effective', e.target.value)}
                                                    placeholder="7-10"
                                                    className="w-full px-3 py-2 text-sm border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <span className="text-xs text-red-700 font-semibold uppercase block mb-1">
                                                    ❌ Неэффективный
                                                </span>
                                                <input
                                                    type="text"
                                                    value={criterionData.ineffective || ''}
                                                    onChange={(e) => handleCriterionChange(key, 'ineffective', e.target.value)}
                                                    placeholder="1-6"
                                                    className="w-full px-3 py-2 text-sm border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Футер с кнопками */}
                <div className="border-t border-gray-200 p-6 bg-gray-50 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Закрыть
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                        {isSaving && (
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                        {isSaving ? 'Сохранение...' : 'Сохранить все типы'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EfficiencyModal;
